/**
 * Типы биллинга — синхронизированы с бэком B-MVP-1
 * (richat_backend/api/routers/billing.py).
 *
 * Все суммы (amount_rub, balance_rub) могут прийти строкой из-за
 * Pydantic Decimal → JSON-сериализация. Приводим к number в селекторах
 * через toNumber() где нужны вычисления.
 */

export interface TopupCreateRequest {
  /** V2-1: пополняется аккаунт; ассистент необязателен. */
  assistant_id?: string;
  amount_rub: number;
}

export interface TopupCreateResponse {
  payment_intent_id: string;
  /** URL на который делаем window.location для оплаты на стороне ЮKассы. */
  confirmation_url: string;
}

export type PaymentIntentStatus = 'pending' | 'succeeded' | 'canceled' | 'failed';

export interface PaymentIntentStatusResponse {
  id: string;
  status: PaymentIntentStatus;
  amount_rub: number | string;
  paid_at: string | null;
  fail_reason: string | null;
}

export interface BillingTransaction {
  id: string;
  kind: string;
  amount_rub: number | string;
  balance_after_rub: number | string;
  created_at: string;
}

/** GET /billing/summary — баланс аккаунта (V2-1). */
export interface AccountBillingSummary {
  balance_rub: number | string;
  recent_transactions: BillingTransaction[];
}

export interface AssistantBillingSummary {
  assistant_id: string;
  balance_rub: number | string;
  recent_transactions: BillingTransaction[];
}

/** Транзакция в общей истории юзера — с именем ассистента. */
export interface UserTransaction {
  id: string;
  assistant_id: string | null;
  assistant_name: string | null;
  kind: string;
  amount_rub: number | string;
  balance_after_rub: number | string;
  created_at: string;
  /** V2-1: канал (test_chat | telegram | max | widget | ingest), модель, токены. */
  source?: string | null;
  model?: string | null;
  tokens_in?: number | null;
  tokens_out?: number | null;
}

export interface UserTransactionsResponse {
  transactions: UserTransaction[];
  has_more: boolean;
}

/**
 * Фильтры для GET /billing/transactions (B-MVP-4a).
 * Все опциональны. Даты — ISO-строки (date_from/date_to).
 */
export interface TransactionFilters {
  assistant_id?: string;
  kind?: string;
  date_from?: string;
  date_to?: string;
}

/* ── Сводка биллинга (B-MVP-4a: GET /billing/overview) ──────────────
 * Формы 1-в-1 с бэк-схемами BillingOverviewResponse / ChannelSpend /
 * AssistantSpend / DaySpend. Все суммы приходят строкой (Pydantic
 * Decimal → JSON), приводим к number через toNumber() при отображении.
 */

/** Расход по каналу (ChannelSpend). platform: telegram|max|widget. */
export interface ChannelSpend {
  platform: string;
  messages_count: number;
  spent_rub: number | string;
  tokens_in?: number;
  tokens_out?: number;
}

/** Расход по ассистенту (AssistantSpend). */
export interface AssistantSpend {
  assistant_id: string | null;
  assistant_name: string | null;
  spent_rub: number | string;
}

/** Расход за день (DaySpend). date — ISO-строка дня (YYYY-MM-DD). */
export interface DaySpend {
  date: string;
  spent_rub: number | string;
}

/** Агрегированная сводка биллинга за период (BillingOverviewResponse). */
export interface BillingOverviewResponse {
  date_from: string | null;
  date_to: string | null;
  /** Пополнения картой (kind topup_paid). */
  total_paid_rub: number | string;
  /** Приветственный бонус (kind topup + meta.reason=welcome). */
  total_welcome_rub: number | string;
  /** V2-1: ручные корректировки (admin_adjustment), со знаком. */
  total_adjustment_rub?: number | string;
  /** Расход за чат (channel_message_*). */
  total_chat_rub: number | string;
  /** Расход за базу знаний (page_ingest + image_ingest). */
  total_ingest_rub: number | string;
  /** Сумма расхода (chat + ingest). */
  total_spent_rub: number | string;
  /** V2-1: токены за период. */
  total_tokens_in?: number;
  total_tokens_out?: number;
  by_channel: ChannelSpend[];
  by_assistant: AssistantSpend[];
  by_day: DaySpend[];
}

/**
 * Минимальная сумма платного пополнения (синхронно с бэком).
 * Серия C: опущено с 15000 до 1000. Нигде явно в UI не показываем —
 * всплывает только при попытке внести меньше.
 */
export const MIN_TOPUP_RUB = 1000;
export const MAX_TOPUP_RUB = 1_000_000;

/**
 * Безопасное приведение Decimal-строки к number.
 * Бэк может прислать "15000.00" или 15000 — оба валидны.
 */
export function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Человеко-читаемые лейблы для kind транзакций.
 *
 * ⚠️ Категоризация из diagnostic 21 мая (CLAUDE.md раздел 5):
 * - welcome это kind 'topup' с meta.reason='welcome' (НЕ 'topup_welcome'!).
 *   Отдельный лейбл welcomeTransactionLabel ниже это учитывает.
 * - тип канала зашит в kind: channel_message_{platform}.
 * - расход KB: page_ingest / image_ingest.
 * - adjustment/refund — служебные (hold/capture механика ингеста).
 */
export const TRANSACTION_KIND_LABELS: Record<string, string> = {
  answer: 'Ответ ассистента',
  rewrite: 'Уточнение вопроса',
  account_open: 'Открытие единого баланса',
  topup_paid: 'Пополнение картой',
  topup: 'Приветственный бонус',
  topup_autopay: 'Автопополнение',
  channel_message_max: 'Чат (MAX)',
  channel_message_telegram: 'Чат (Telegram)',
  channel_message_widget: 'Чат (виджет)',
  page_ingest: 'Обработка страниц',
  image_ingest: 'Обработка картинок',
  adjustment: 'Резерв средств',
  refund: 'Возврат резерва',
  paid_expired: 'Истечение баланса',
  admin_adjustment: 'Корректировка',
};

/**
 * meta.reason у welcome-бонуса.
 * topup с этим reason — это приветственный бонус, не платное пополнение.
 */
export const WELCOME_TOPUP_REASON = 'welcome';

/** Каналы для строк по токенам и сводки (V2-1). */
export const SOURCE_LABELS: Record<string, string> = {
  test_chat: 'тест-чат',
  telegram: 'Telegram',
  max: 'MAX',
  widget: 'виджет',
  max_operator: 'MAX (оператор)',
  api: 'API',
  ingest: 'база знаний',
};

export function sourceLabel(source: string | null | undefined): string {
  if (!source) return '';
  return SOURCE_LABELS[source] ?? source;
}

export function transactionKindLabel(kind: string, source?: string | null): string {
  const base = TRANSACTION_KIND_LABELS[kind] ?? kind;
  if ((kind === 'answer' || kind === 'rewrite') && source) {
    return `${base} (${sourceLabel(source)})`;
  }
  return base;
}

/** Форматирование числа токенов: 12 345. */
export function formatTokens(n: number | null | undefined): string {
  return (n ?? 0).toLocaleString('ru-RU');
}
